import { useCallback } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import type { ListEntityImagesResponse } from "./entityImageApi";
import type { EntityImageListItem } from "./types";

export interface UseEntityImagesResult {
  images: EntityImageListItem[];
  // Total across all pages, unlike images.length which only counts pages fetched so far.
  totalAlternateCount: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

function entityImagesQueryKey(queryKeyPrefix: string, entityId: string) {
  return [queryKeyPrefix, entityId] as const;
}

interface UseEntityImagesParams {
  /** First query-key segment, e.g. "characterPortraits"; the entity id is the second. */
  queryKeyPrefix: string;
  entityId: string;
  fetchPage: (entityId: string, cursor: string | undefined) => Promise<ListEntityImagesResponse>;
  enabled: boolean;
  /** Sentry `feature` tag for list failures, e.g. "character_portrait_list". */
  sentryFeature: string;
}

// Images only change through explicit save/generate/delete actions, all of which invalidate
// this query — so staleTime: Infinity means reopening an entity never re-fetches (and never
// re-shows the loading skeleton) unless something actually changed the list. An invalidated
// refetch always restarts from the first (cursor-less) page.
export function useEntityImages({ queryKeyPrefix, entityId, fetchPage, enabled, sentryFeature }: UseEntityImagesParams): UseEntityImagesResult {
  const queryClient = useQueryClient();
  const queryKey = entityImagesQueryKey(queryKeyPrefix, entityId);

  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }: { pageParam?: string }) => fetchPage(entityId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: ListEntityImagesResponse) => (lastPage.pagination.hasMore ? (lastPage.pagination.nextCursor ?? undefined) : undefined),
    // The cover only ever arrives on the first (cursor-less) page — later pages paginate
    // alternates only — so it's merged in front of the flattened alternates here.
    select: (data) => {
      const cover = data.pages[0]?.cover ?? null;
      const alternates = data.pages.flatMap((page) => page.images);
      return { ...data, images: cover ? [cover, ...alternates] : alternates, totalAlternateCount: data.pages[0]?.pagination.totalAlternateCount ?? 0 };
    },
    enabled,
    staleTime: Infinity,
    // Never persisted to localStorage (see query-client.ts's shouldDehydrateQuery) — combined with
    // staleTime: Infinity, a rehydrated-but-stale snapshot would never self-correct, since this
    // query only ever updates via explicit invalidateQueries calls, not automatic refetching.
    meta: { persist: false },
    onError: (err) => {
      Sentry.captureException(err, { tags: { feature: sentryFeature } });
    },
  });

  const refetch = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: entityImagesQueryKey(queryKeyPrefix, entityId) });
  }, [queryClient, queryKeyPrefix, entityId]);

  return {
    images: query.data?.images ?? [],
    totalAlternateCount: query.data?.totalAlternateCount ?? 0,
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : query.error ? "Couldn't load images" : null,
    refetch,
    hasNextPage: !!query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: () => {
      void query.fetchNextPage();
    },
  };
}
